import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ExternalLink,
  Star,
  AlertTriangle,
  Search,
  ThumbsUp,
  ThumbsDown,
  Sparkles,
  MapPin,
  ImageOff,
  ArrowRight,
  LayoutGrid,
  Rows3,
  Check,
  X as XIcon,
  Circle,
  Pencil,
  Plus,
  Trash2,
  ArrowUp,
  Upload,
} from "lucide-react";
import api from "../lib/api";
import ClientLogo from "../components/ClientLogo";
import ClientSelect from "../components/ClientSelect";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

// =============================================================================
// Portfolio — one component, four routes
// =============================================================================
//   /portfolio               all 3D twins
//   /portfolio/vexos         twins, VEXOS only        <- menu points here
//   /portfolio/smartorange   twins, SmartOrange only  <- and here
//   /portfolio/studio        websites + renders
//
// The brand split is REAL ROUTES, not JS tabs: you land on your own brand
// instead of clicking a filter every single visit. Same reason studio work has
// its own URL — 3D twins are the product, sites and renders are not, and 99
// twins once sat buried under 93 websites in a single list.
//
// Grid or table, whichever suits the moment; the choice sticks in localStorage.
// Grid is for looking at the work, table is for working through it.
//
// Link state is MARKED BY HAND (link_ok: true/false/null). An automatic checker
// shipped here first and was removed: from the office the network blocks DNS
// for our own domains, so it reported live client sites as dead. A portfolio
// that libels working links is worse than one that says "not checked".
//
// Review mirrors Dmitry's table 1:1 — score 1-5, what worked, what to drop.
// A human verdict (`review`) and an AI draft (`ai_review`) never share a field,
// and "flythrough score" is meaningless for a website, so the studio section
// shows no score at all.
//
// Theme note: this project aliases ALL 21 colour scales onto CSS vars and
// inverts them in light mode — accent tints must be translucent-500
// (bg-emerald-500/10), never emerald-50 + dark:.
// =============================================================================

const BRAND_LABEL = { trp: "VEXOS", smartorange: "SmartOrange", other: "Other" };

const SCORE_CHIP = {
  5: "bg-emerald-500/20 text-emerald-300",
  4: "bg-emerald-500/15 text-emerald-300",
  3: "bg-amber-500/20 text-amber-300",
  2: "bg-orange-500/20 text-orange-300",
  1: "bg-red-500/20 text-red-300",
};

const REVIEW_FILTERS = [
  { key: "all", label: "All" },
  { key: "unreviewed", label: "Not reviewed" },
  { key: "reviewed", label: "Reviewed" },
  { key: "broken", label: "Marked broken" },
  { key: "unmarked", label: "Link unmarked" },
];

// Studio work is a mixed bag, so each kind gets its own tab with a count —
// an undivided list of 127 sites, render hand-offs and link-less leftovers is
// unreadable. `video` is a real category with (so far) nothing in it: no source
// we imported carries a video link. Renders arrived as Google Drive folders,
// which may well contain video, but we only ever got the folder.
const STUDIO_FILTERS = [
  { key: "studio", label: "All studio work", count: null },
  { key: "website", label: "Websites", count: "website" },
  { key: "render", label: "Renders", count: "render" },
  { key: "video", label: "Video", count: "video" },
  { key: "unclassified", label: "No link yet", count: "unclassified" },
];

const VIEW_KEY = "portfolio.view";

// Who may edit the catalogue — mirrors PORTFOLIO_WRITE_ROLES in
// backend/portfolio.py. Everyone on staff can still score and mark links; only
// the record itself (title, links, where a work belongs) is narrower.
const WRITE_ROLES = ["admin", "pm_lead", "pm", "art_director", "crm_manager"];

const CATEGORY_LABEL = {
  "3d_twin": "3D twin — interactive tour",
  website: "Website",
  render: "Renders",
  video: "Video",
  unclassified: "No link yet",
};

// The link that opens the tour is the point of a twin: a marketing site in this
// field makes the portfolio show a landing page where a flythrough belongs. The
// site keeps its place under "Other links" — it is just not what we show off.
const PRIMARY_HINT = {
  "3d_twin": "The interactive tour itself (usually …/3d/…), not the project's website",
  website: "The site we built",
  render: "Where the renders live — a Drive folder counts",
  video: "The video",
  unclassified: "Any link, once there is one",
};

function linesToList(text) {
  return (text || "")
    .split("\n")
    .map((s) => s.replace(/^[-•]\s*/, "").trim())
    .filter(Boolean);
}

function Stat({ label, value, tone = "text-zinc-100" }) {
  return (
    <div className="px-4 py-3">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`text-xl font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

// Three states, one control: unmarked -> works -> broken -> unmarked.
// Deliberately not a checkbox — "nobody looked" is a real state and must not
// look like "fine".
function LinkMark({ work, onMarked, compact }) {
  const [busy, setBusy] = useState(false);
  const state = work.link_ok === true ? "ok" : work.link_ok === false ? "broken" : "unmarked";
  const next = { unmarked: true, ok: false, broken: null }[state];

  const cycle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.put(`/portfolio/${work.id}/link`, { works: next });
      onMarked(work.id, next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark");
    } finally {
      setBusy(false);
    }
  };

  const look = {
    ok: { Icon: Check, cls: "bg-emerald-500/20 text-emerald-300", label: "works" },
    broken: { Icon: XIcon, cls: "bg-red-500/20 text-red-300", label: "broken" },
    unmarked: { Icon: Circle, cls: "bg-zinc-500/20 text-zinc-400", label: "not checked" },
  }[state];
  const { Icon, cls, label } = look;

  if (!work.url) {
    return (
      <span
        className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${
          compact ? "" : "backdrop-blur"
        } bg-amber-500/20 text-amber-300`}
      >
        <AlertTriangle className="h-3 w-3" /> no URL
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={cycle}
      disabled={busy}
      title="Click to mark: works / broken / not checked"
      className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition hover:brightness-125 ${cls} ${
        compact ? "" : "backdrop-blur"
      }`}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      {label}
    </button>
  );
}

function ScorePicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          className={`h-9 w-9 rounded-md border text-sm font-medium transition ${
            value === n
              ? "border-sky-500 bg-sky-500/15 text-sky-400"
              : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
          }`}
        >
          {n}
        </button>
      ))}
      {value ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-2 text-xs text-zinc-500 hover:text-zinc-300"
        >
          clear
        </button>
      ) : null}
    </div>
  );
}

function ReviewDialog({ work, open, onOpenChange, onSaved }) {
  const [score, setScore] = useState(null);
  const [good, setGood] = useState("");
  const [bad, setBad] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!work) return;
    const r = work.review || {};
    setScore(r.score || null);
    setGood((r.good || []).join("\n"));
    setBad((r.bad || []).join("\n"));
    setNotes(r.notes || "");
  }, [work]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/portfolio/${work.id}/review`, {
        score,
        good: linesToList(good),
        bad: linesToList(bad),
        notes,
      });
      toast.success("Review saved");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!work) return null;
  const ai = work.ai_review || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {work.title}
            {work.url ? (
              <a
                href={work.url}
                target="_blank"
                rel="noreferrer"
                className="text-sky-400 hover:text-sky-300"
                title="Open flythrough"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mb-2 text-sm text-zinc-400">Flythrough score</div>
            <ScorePicker value={score} onChange={setScore} />
          </div>

          <div>
            <div className="mb-1 flex items-center gap-2 text-sm text-emerald-400">
              <ThumbsUp className="h-4 w-4" /> What worked — decisions to keep
            </div>
            <Textarea
              rows={4}
              value={good}
              onChange={(e) => setGood(e.target.value)}
              placeholder="One point per line"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center gap-2 text-sm text-red-400">
              <ThumbsDown className="h-4 w-4" /> What to stop doing
            </div>
            <Textarea
              rows={4}
              value={bad}
              onChange={(e) => setBad(e.target.value)}
              placeholder="One point per line"
            />
          </div>

          <div>
            <div className="mb-1 text-sm text-zinc-400">Notes</div>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {ai.score || (ai.good || []).length ? (
            <div className="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-violet-400">
                <Sparkles className="h-3.5 w-3.5" /> AI draft — not a human verdict
              </div>
              {ai.score ? <div className="text-sm text-zinc-300">Score: {ai.score}</div> : null}
              {(ai.good || []).map((g, i) => (
                <div key={`ag${i}`} className="text-sm text-emerald-400">+ {g}</div>
              ))}
              {(ai.bad || []).map((b, i) => (
                <div key={`ab${i}`} className="text-sm text-red-400">− {b}</div>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children, className }) {
  return (
    <div className={className}>
      <div className="mb-1 text-sm text-zinc-400">{label}</div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-zinc-500">{hint}</div> : null}
    </div>
  );
}

// Add a work, or fix the record of one. `work === null` means "new".
//
// The whole reason this exists: works arrived from a Google table where the
// "link" column often held the client's website, so a twin could point at a
// landing page instead of the tour. Fixing that used to mean a database call.
// Extra links get a "make primary" arrow because the right URL is usually
// already sitting in the row — Riverport's tour was in the second column all
// along.
function WorkFormDialog({ work, open, onOpenChange, onSaved, defaultCategory, defaultBrand }) {
  const isNew = !work;
  const [form, setForm] = useState({});
  const [extra, setExtra] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Own endpoint rather than /clients-database: an art director may edit the
  // catalogue but has no access to the client database and its revenue.
  const [clientOptions, setClientOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    const w = work || {};
    setForm({
      title: w.title || "",
      url: w.url || "",
      category: w.category || defaultCategory || "3d_twin",
      brand: w.brand || defaultBrand || "other",
      client_id: w.client_id || "",
      client_name: w.client_name || "",
      location: w.location || "",
      year: w.year ? String(w.year) : "",
      cover_url: w.cover_url || "",
    });
    setExtra((w.urls || []).join("\n"));
    setConfirmDelete(false);
    api
      .get("/portfolio-clients")
      .then((res) => setClientOptions(res.data?.clients || []))
      .catch(() => setClientOptions([]));
  }, [work, open, defaultCategory, defaultBrand]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const extraLinks = useMemo(() => linesToList(extra), [extra]);

  // Promote a secondary link and demote the current primary — never lose the
  // old address, it is how someone recognises the project a year from now.
  const makePrimary = (link) => {
    const rest = extraLinks.filter((l) => l !== link);
    if (form.url) rest.unshift(form.url);
    set("url", link);
    setExtra(rest.join("\n"));
  };

  // The file goes straight to the work — the server stores it and answers with
  // the path, so the field fills itself and a plain Save is not required for
  // the picture to stick.
  const uploadCover = async (file) => {
    if (!file || !work) return;
    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    try {
      const res = await api.post(`/portfolio/${work.id}/cover`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("cover_url", `${res.data.cover_url}?v=${Date.now()}`);
      toast.success(`Cover uploaded — ${Math.round(res.data.bytes / 1024)} KB`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("A title is required");
      return;
    }
    const year = form.year.trim() ? Number(form.year.trim()) : null;
    if (form.year.trim() && (!Number.isInteger(year) || year < 1990 || year > 2100)) {
      toast.error("Year looks wrong");
      return;
    }
    const payload = {
      title: form.title.trim(),
      url: form.url.trim(),
      urls: extraLinks,
      category: form.category,
      brand: form.brand,
      client_id: form.client_id || "",
      client_name: (form.client_name || "").trim(),
      location: form.location.trim(),
      // strip the cache-buster the upload adds for the preview
      cover_url: form.cover_url.trim().split("?v=")[0],
      ...(year ? { year } : {}),
    };
    setSaving(true);
    try {
      if (isNew) await api.post("/portfolio", payload);
      else await api.put(`/portfolio/${work.id}`, payload);
      toast.success(isNew ? "Work added" : "Saved");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/portfolio/${work.id}`);
      toast.success("Work archived");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add a work" : `Edit — ${work.title}`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Title">
              <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Section">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Main link" hint={PRIMARY_HINT[form.category]}>
            <Input
              value={form.url || ""}
              onChange={(e) => set("url", e.target.value)}
              placeholder="https://…"
            />
          </Field>

          <div>
            <div className="mb-1 text-sm text-zinc-400">Other links — one per line</div>
            <Textarea
              rows={3}
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="The project's website, an older address, a Drive folder…"
            />
            {extraLinks.length ? (
              <div className="mt-1.5 space-y-1">
                {extraLinks.map((l) => (
                  <div key={l} className="flex items-center gap-2 text-[11px]">
                    <button
                      type="button"
                      onClick={() => makePrimary(l)}
                      title="Make this the main link"
                      className="flex items-center gap-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-sky-400 hover:bg-sky-500/25"
                    >
                      <ArrowUp className="h-3 w-3" /> make main
                    </button>
                    <span className="truncate text-zinc-500">{l}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Client takes a double column: it is the only cell here holding a
              company name, and at 1/4 width "BI Group White Garden" truncated
              to "BI G…" tells the reader nothing. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
            <Field label="Brand">
              <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(BRAND_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Client" className="sm:col-span-2">
              <ClientSelect
                clients={clientOptions}
                valueId={form.client_id}
                valueName={form.client_name}
                onChange={({ id, name }) =>
                  setForm((f) => ({ ...f, client_id: id, client_name: name }))
                }
              />
            </Field>
            <Field label="Location">
              <Input
                value={form.location || ""}
                onChange={(e) => set("location", e.target.value)}
              />
            </Field>
            <Field label="Year">
              <Input
                value={form.year || ""}
                onChange={(e) => set("year", e.target.value)}
                placeholder="2025"
              />
            </Field>
          </div>

          <Field
            label="Cover image"
            hint={
              isNew
                ? "Paste a link now; upload a file after the work exists."
                : "Upload a file, or paste a link. Uploads are resized to 960 px WebP and stored with us."
            }
          >
            <div className="flex gap-2">
              <Input
                value={form.cover_url || ""}
                onChange={(e) => set("cover_url", e.target.value)}
                placeholder="https://… or /uploads/portfolio/….webp"
              />
              {!isNew ? (
                <>
                  <input
                    id="cover-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadCover(e.target.files?.[0])}
                  />
                  <Button
                    variant="outline"
                    disabled={uploading}
                    onClick={() => document.getElementById("cover-file")?.click()}
                    className="shrink-0"
                  >
                    {uploading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Upload
                  </Button>
                </>
              ) : null}
            </div>
            {form.cover_url ? (
              <img
                src={form.cover_url}
                alt=""
                className="mt-2 h-24 rounded border border-zinc-800 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}
          </Field>

          {!isNew && form.url && form.url !== (work.url || "") ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-400">
              The link changed — its “works / broken” mark resets to unchecked, and the old
              address is kept so the next import does not put it back.
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {isNew ? (
            <span />
          ) : (
            <Button
              variant="outline"
              onClick={remove}
              disabled={saving}
              className={confirmDelete ? "border-red-500/50 text-red-400" : "text-zinc-400"}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {confirmDelete ? "Really archive?" : "Archive"}
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isNew ? "Add" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkTile({ work, showScore, onReview, onMarked, onEdit }) {
  // Covers live on client servers, not ours, so one can 404 after harvest —
  // degrade to the placeholder instead of showing a broken image.
  const [imgFailed, setImgFailed] = useState(false);
  const review = work.review || {};
  const cover = !imgFailed && work.cover_url ? work.cover_url : null;

  return (
    <div className="group relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-500/5 transition hover:border-zinc-600">
      <div className="relative aspect-[16/10] overflow-hidden bg-zinc-500/10">
        {cover ? (
          <img
            src={cover}
            alt={work.title}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-zinc-600">
            <ImageOff className="h-6 w-6" />
            <span className="text-[11px]">no preview</span>
          </div>
        )}

        {/* legibility floor for the caption, whatever the render underneath */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 to-transparent" />

        {showScore && review.score ? (
          <span
            className={`absolute left-2 top-2 rounded px-2 py-0.5 text-sm font-semibold backdrop-blur ${SCORE_CHIP[review.score]}`}
          >
            {review.score}
          </span>
        ) : null}

        <div className="absolute right-2 top-2">
          <LinkMark work={work} onMarked={onMarked} />
        </div>

        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="truncate text-sm font-medium text-white drop-shadow">{work.title}</div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-zinc-300/90">
            {/* The mark rides along even when `location` takes the text slot —
                on a wall of tiles it is the fastest way to read whose work it
                is. onDark: the caption sits on a black gradient in both themes. */}
            {work.client_logo_url ? (
              <ClientLogo
                src={work.client_logo_url}
                variant={work.client_logo_variant}
                name={work.client_name}
                onDark
                className="h-5 w-12 rounded-none border-0 bg-transparent drop-shadow"
                iconClassName="h-3 w-3"
              />
            ) : null}
            {work.location ? (
              <span className="flex min-w-0 items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{work.location}</span>
              </span>
            ) : work.client_name ? (
              <span className="truncate">{work.client_name}</span>
            ) : null}
            {work.year ? <span className="shrink-0">{work.year}</span> : null}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2">
        <span className="rounded bg-zinc-500/15 px-1.5 py-0.5 text-[11px] text-zinc-400">
          {BRAND_LABEL[work.brand] || work.brand}
        </span>
        {work.property_type ? (
          <span className="truncate text-[11px] text-zinc-500">{work.property_type}</span>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          {work.url ? (
            <a
              href={work.url}
              target="_blank"
              rel="noreferrer"
              title="Open"
              className="rounded p-1.5 text-zinc-500 hover:bg-zinc-500/15 hover:text-sky-400"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              onClick={() => onEdit(work)}
              title="Edit links and details"
              className="rounded p-1.5 text-zinc-500 hover:bg-zinc-500/15 hover:text-zinc-200"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : null}
          {showScore ? (
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onReview(work)}>
              <Star className="mr-1 h-3 w-3" />
              {review.score ? "Edit" : "Review"}
            </Button>
          ) : null}
        </div>
      </div>

      {showScore && ((review.good || []).length || (review.bad || []).length) ? (
        <div className="space-y-0.5 border-t border-zinc-800 px-3 py-2">
          {(review.good || []).slice(0, 2).map((g, i) => (
            <div key={`g${i}`} className="truncate text-[11px] text-emerald-400">+ {g}</div>
          ))}
          {(review.bad || []).slice(0, 2).map((b, i) => (
            <div key={`b${i}`} className="truncate text-[11px] text-red-400">− {b}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function WorkTable({ works, showScore, onReview, onMarked, onEdit }) {
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500">
              {showScore ? <th className="w-12 px-3 py-2">Score</th> : null}
              <th className="px-3 py-2">Work</th>
              <th className="w-40 px-3 py-2">Client / location</th>
              <th className="w-28 px-3 py-2">Brand</th>
              <th className="w-28 px-3 py-2">Link</th>
              <th className="w-24 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {works.map((w) => {
              const review = w.review || {};
              return (
                <tr key={w.id} className="border-b border-zinc-800 last:border-0 hover:bg-zinc-500/5">
                  {showScore ? (
                    <td className="px-3 py-2">
                      {review.score ? (
                        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${SCORE_CHIP[review.score]}`}>
                          {review.score}
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  ) : null}
                  <td className="px-3 py-2">
                    <div className="font-medium text-zinc-100">{w.title}</div>
                    {w.url ? (
                      <a
                        href={w.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block max-w-[34rem] truncate text-xs text-sky-400/80 hover:text-sky-300"
                      >
                        {w.url}
                      </a>
                    ) : (
                      <span className="text-xs text-zinc-600">{w.source_raw || "no link"}</span>
                    )}
                    {showScore && ((review.good || []).length || (review.bad || []).length) ? (
                      <div className="mt-1 space-y-0.5">
                        {(review.good || []).map((g, i) => (
                          <div key={`g${i}`} className="text-xs text-emerald-400">+ {g}</div>
                        ))}
                        {(review.bad || []).map((b, i) => (
                          <div key={`b${i}`} className="text-xs text-red-400">− {b}</div>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400">
                    <div className="flex items-center gap-2">
                      {w.client_logo_url ? (
                        <ClientLogo
                          src={w.client_logo_url}
                          variant={w.client_logo_variant}
                          name={w.client_name}
                          className="h-6 w-10"
                          iconClassName="h-3 w-3"
                        />
                      ) : null}
                      <div className="truncate">{w.client_name || "—"}</div>
                    </div>
                    {w.location ? <div className="truncate text-zinc-500">{w.location}</div> : null}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-zinc-500/15 px-1.5 py-0.5 text-[11px] text-zinc-400">
                      {BRAND_LABEL[w.brand] || w.brand}
                    </span>
                    {w.year ? <span className="ml-2 text-xs text-zinc-500">{w.year}</span> : null}
                  </td>
                  <td className="px-3 py-2">
                    <LinkMark work={w} onMarked={onMarked} compact />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {onEdit ? (
                        <button
                          type="button"
                          onClick={() => onEdit(w)}
                          title="Edit links and details"
                          className="rounded p-1.5 text-zinc-500 hover:bg-zinc-500/15 hover:text-zinc-200"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                      {showScore ? (
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onReview(w)}>
                          <Star className="mr-1 h-3 w-3" />
                          {review.score ? "Edit" : "Review"}
                        </Button>
                      ) : w.url ? (
                        <a
                          href={w.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block rounded p-1.5 text-zinc-500 hover:bg-zinc-500/15 hover:text-sky-400"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function PortfolioPage({ section = "twins", brand: fixedBrand = null }) {
  const isTwins = section === "twins";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(isTwins ? "all" : "studio");
  // Only on the cross-brand view: the per-brand routes are the filter.
  const [brand, setBrand] = useState("");
  const [q, setQ] = useState("");
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");
  const [reviewWork, setReviewWork] = useState(null);
  // null = closed, {} = adding, a work = editing it.
  const [formWork, setFormWork] = useState(null);

  const { user } = useAuth();
  const canEdit = WRITE_ROLES.includes(user?.role);

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (isTwins) {
        params.category = "3d_twin";
        if (fixedBrand) params.brand = fixedBrand;
        else if (brand) params.brand = brand;
        if (filter === "reviewed") params.reviewed = true;
        if (filter === "unreviewed") params.reviewed = false;
        if (filter === "broken") params.link = "broken";
        if (filter === "unmarked") params.link = "unmarked";
      } else {
        params.category = filter; // studio | website | render | unclassified
      }
      const res = await api.get("/portfolio", { params });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load portfolio");
    } finally {
      setLoading(false);
    }
  }, [filter, isTwins, fixedBrand, brand]);

  useEffect(() => {
    load();
  }, [load]);

  // Patch in place rather than refetching — marking 100 links should not cost
  // 100 round trips through the whole list.
  const onMarked = useCallback((id, works) => {
    setData((prev) =>
      prev
        ? { ...prev, works: prev.works.map((w) => (w.id === id ? { ...w, link_ok: works } : w)) }
        : prev
    );
  }, []);

  const works = useMemo(() => {
    const list = data?.works || [];
    if (!q.trim()) return list;
    const needle = q.toLowerCase().trim();
    return list.filter(
      (w) =>
        (w.title || "").toLowerCase().includes(needle) ||
        (w.client_name || "").toLowerCase().includes(needle) ||
        (w.location || "").toLowerCase().includes(needle) ||
        (w.url || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const stats = data?.stats;
  const counts = data?.counts_by_category || {};
  const studioTotal =
    (counts.website || 0) + (counts.render || 0) + (counts.video || 0) + (counts.unclassified || 0);
  const filters = isTwins ? REVIEW_FILTERS : STUDIO_FILTERS;

  const heading = !isTwins
    ? "Studio work"
    : fixedBrand
    ? `${BRAND_LABEL[fixedBrand]} — 3D Twins`
    : "3D Twins";

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">{heading}</h1>
          <p className="text-sm text-zinc-500">
            {isTwins
              ? "The product — every interactive tour we shipped, and how the flythrough turned out"
              : "Websites and renders — everything that is not a 3D twin"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {canEdit ? (
            <Button size="sm" onClick={() => setFormWork({})}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add work
            </Button>
          ) : null}
          {isTwins ? (
            <Link
              to="/portfolio/studio"
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-500/10 hover:text-zinc-200"
            >
              Studio work
              <span className="text-zinc-600">{studioTotal}</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <Link
              to="/portfolio"
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-500/10 hover:text-zinc-200"
            >
              3D Twins
              <span className="text-zinc-600">{counts["3d_twin"] || 0}</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}

          <div className="flex items-center rounded-md border border-zinc-800 p-0.5">
            {[
              { key: "grid", Icon: LayoutGrid, title: "Tiles" },
              { key: "table", Icon: Rows3, title: "Table" },
            ].map(({ key, Icon, title }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                title={title}
                className={`rounded p-1.5 transition ${
                  view === key ? "bg-zinc-500/20 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {stats ? (
        <Card>
          <CardContent className="flex flex-wrap divide-x divide-zinc-800 p-0">
            <Stat label={isTwins ? "Tours" : "Works"} value={stats.total} />
            {isTwins ? (
              <>
                <Stat label="Reviewed" value={stats.reviewed} tone="text-emerald-400" />
                <Stat label="Not reviewed" value={stats.unreviewed} tone="text-amber-400" />
                <Stat label="Avg score" value={stats.avg_score ?? "—"} />
              </>
            ) : null}
            <Stat label="Link works" value={stats.link_ok} tone="text-emerald-400" />
            <Stat
              label="Link broken"
              value={stats.link_broken}
              tone={stats.link_broken > 0 ? "text-red-400" : "text-zinc-100"}
            />
            <Stat label="Not checked" value={stats.link_unmarked} tone="text-zinc-400" />
            <Stat label="No URL" value={stats.no_url} tone="text-amber-400" />
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => {
          const n = f.count ? counts[f.count] : f.key === "studio" ? studioTotal : null;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition ${
                filter === f.key ? "bg-sky-500/15 text-sky-400" : "text-zinc-400 hover:bg-zinc-500/10"
              }`}
            >
              {f.label}
              {n !== null && n !== undefined ? (
                <span className="text-zinc-600">{n}</span>
              ) : null}
            </button>
          );
        })}
        {isTwins && !fixedBrand ? (
          <>
            <div className="mx-1 h-5 w-px bg-zinc-800" />
            {["", "trp", "smartorange"].map((b) => (
              <button
                key={b || "all"}
                onClick={() => setBrand(b)}
                className={`rounded-md px-3 py-1.5 text-sm transition ${
                  brand === b ? "bg-zinc-500/15 text-zinc-100" : "text-zinc-400 hover:bg-zinc-500/10"
                }`}
              >
                {b ? BRAND_LABEL[b] : "All brands"}
              </button>
            ))}
          </>
        ) : null}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            className="w-56 pl-8"
          />
        </div>
      </div>

      {error ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-4 text-red-400">
            <AlertTriangle className="h-4 w-4" /> {error}
          </CardContent>
        </Card>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 p-16 text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : works.length === 0 ? (
        <div className="p-16 text-center text-sm text-zinc-500">Nothing here.</div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {works.map((w) => (
            <WorkTile
              key={w.id}
              work={w}
              showScore={isTwins}
              onReview={setReviewWork}
              onMarked={onMarked}
              onEdit={canEdit ? setFormWork : null}
            />
          ))}
        </div>
      ) : (
        <WorkTable
          works={works}
          showScore={isTwins}
          onReview={setReviewWork}
          onMarked={onMarked}
          onEdit={canEdit ? setFormWork : null}
        />
      )}

      <ReviewDialog
        work={reviewWork}
        open={!!reviewWork}
        onOpenChange={(v) => !v && setReviewWork(null)}
        onSaved={load}
      />

      <WorkFormDialog
        work={formWork && formWork.id ? formWork : null}
        open={!!formWork}
        onOpenChange={(v) => !v && setFormWork(null)}
        onSaved={load}
        defaultCategory={isTwins ? "3d_twin" : filter === "studio" ? "website" : filter}
        defaultBrand={fixedBrand || brand || "other"}
      />
    </div>
  );
}
